import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { LoadingSpinner } from "../../components/LoadingSpinner";
import { MessageType } from "../../types/MessageType";
import { BananaApi } from "../../api/BananaApi";
import toast from "react-hot-toast";
import { BananaType, BananaTypeProps } from "../../types/BananaType";
import { getAuth } from "firebase/auth";
import { UserRole } from "../../types/UserRole";
import { Links } from "../../components/utils/Links";
import { FiMessageSquare, FiMail, FiInstagram, FiLoader } from "react-icons/fi";
import { FaFacebook, FaSnapchatGhost, FaTiktok, FaWhatsapp } from "react-icons/fa";
import { ViewBananaHistory } from "./ViewBananaHistory";
import { SendBananaMessageView } from "../send-banana/SendBananaMessageView";
import { User } from "../../types/User";
import { useAppData } from "../../context/AppContext";
import { animate, AnimationPlaybackControls } from "framer-motion";
import ConfettiCanvas, { shootFireworks } from "../../components/ConfettiCanvas";
import { BananaPassport } from "./BananaPassport";
import { RelayPassport } from "../../relay/RelayTypes";
import { getDefaultMessage, getSuggestedMessages, RELAY_INTENTS, RelayIntent } from "../../relay/RelayPrompts";
import { logRelayEvent } from "../../analytics/RelayAnalytics";

export enum Action {
  View = "view",
  Forward = "forward",
  SendBack = "back",
}

const TITLES: Record<Action, string | undefined> = {
  [Action.View]: undefined,
  [Action.Forward]: "Banana Forward 🍌 Spread the Smile 😁",
  [Action.SendBack]: "Banana Back 🍌 Return the Joy 😄",
}

interface Props { }

const UNWRAP_DELAY = 1900;
const WRAP_DELAY = 1800
const wait = (milliseconds: number) => new Promise((resolve) => setTimeout(resolve, milliseconds));

const ViewBananaPage = ({ }: Props) => {

  const params = useParams();

  const publicId = params.publicId;
  const privateId = params.privateId;

  const searchParams = new URLSearchParams(window.location.search);
  const password = searchParams.get("p");

  const navigate = useNavigate();
  const isFetching = useRef(false);

  const [banana, setBanana] = useState<any>(null);
  const [bananaOperations, setBananaOperations] = useState<Array<any>>([]);
  const [isWrapped, setIsWrapped] = useState(false);
  const [role, setRole] = useState(UserRole.Guest);
  const [canBananaBack, setCanBananaBack] = useState(false);
  const [action, setAction] = useState<Action>(Action.View);
  const { data, setData } = useAppData();

  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingAction, setIsLoadingAction] = useState(false);
  const funMessageIndex = useRef(Math.floor(Math.random() * MessageType.SEND_BANANA_FUN_MESSAGES.length));

  const [senderName, setSenderName] = useState(data?.user?.name || "");
  const [recipientName, setRecipientName] = useState("");
  const [message, setMessage] = useState("");
  const [showWrapingAnimation, setShowWrapingAnimation] = useState(false);
  const [passport, setPassport] = useState<RelayPassport | null>(null);
  const [relayIntent, setRelayIntent] = useState<RelayIntent>("appreciate");
  const [reactionSent, setReactionSent] = useState<string | null>(null);
  const [isSendingReaction, setIsSendingReaction] = useState(false);
  const [publicMessage, setPublicMessage] = useState("");
  const [isPublishingJoy, setIsPublishingJoy] = useState(false);
  const [isJoyPublished, setIsJoyPublished] = useState(false);

  const bananaImgRef = useRef<HTMLImageElement>(null);
  const bananaPresentImgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    if (senderName.trim() === "" && data?.user?.name) {
      setSenderName(data.user.name);
    }
  }, [data?.user?.name]);

  const sendBanana = async () => {
    setIsLoadingAction(true);
    setShowWrapingAnimation(true);

    try {
      let actionRequest: any = null

      if (action === Action.Forward) {
        actionRequest = BananaApi.createRelayChild(
          privateId || "",
          publicId || "",
          password || "",
          senderName,
          recipientName,
          message,
          relayIntent
        );
      } else if (action === Action.SendBack) {
        actionRequest = BananaApi.bananaBack(
          privateId || "",
          publicId || "",
          password || "",
          senderName,
          recipientName,
          message
        );
      } else {
        throw new Error("Invalid action for sending banana");
      }

      const [res] = await Promise.all([actionRequest, wait(WRAP_DELAY)]);

      if (res.user) {
        setData((prevData) => ({
            ...prevData,
            user: res.user,
          }));
      }

      if (action === Action.Forward) {
        logRelayEvent("relay_child_created", {
          intent: relayIntent,
          generation: res.banana?.generation,
        });
      }
      window.location.href = Links.getLinkToViewBanana(res.banana, res.bananaOperation?.password ?? "");
    } catch (error: any) {
      toast.error("Couldn't wrap your banana. " + error.message);
    } finally {
      setShowWrapingAnimation(false);
      setIsLoadingAction(false);
    }
  }
  
  const unwrapBanana = async () => {
    setIsLoadingAction(true);
    logRelayEvent("banana_unwrap_started", { banana_type: banana?.type });

    try {
      const unwrapRequest = BananaApi.unwrapBanana(
        privateId ?? "",
        publicId ?? "",
        password || ""
      );

      setShowWrapingAnimation(true);

      const [res] = await Promise.all([unwrapRequest, wait(UNWRAP_DELAY)]);
      if (res.isWrapped) {
        toast.error("Couldn't unwrap your banana.");
        return;
      }

      setIsWrapped(res.isWrapped);
      setCanBananaBack(res.canBananaBack);
      setBananaOperations((prev) => [res.bananaOperation, ...prev]);
      logRelayEvent("banana_unwrap_completed", { banana_type: banana?.type });
      BananaApi.getRelayPassport(privateId || "", publicId || "", password || "")
        .then(setPassport)
        .catch(() => null);

    } catch (error: any) {
      toast.error("Couldn't unwrap your banana. " + error.message);
      setIsLoading(false);
    } finally {
      setShowWrapingAnimation(false);
      setIsLoadingAction(false);
    }
  }

  useEffect(() => {
    if (!showWrapingAnimation) return;

    const bananaEl = bananaImgRef.current;
    const giftEl = bananaPresentImgRef.current;
    if (!bananaEl || !giftEl) return;

    giftEl.classList.remove("anticipation-shake");

    // reset state
    giftEl.style.opacity = "1";
    giftEl.style.transform = "rotate(0deg)";
    giftEl.style.display = "block";

    bananaEl.style.opacity = "0";
    bananaEl.style.transform = "rotate(0deg)";
    bananaEl.style.display = "block";

    // banana: tilt left -> spin fast -> fade out
    const bananaAnim = animate(
      giftEl,
      {
        rotate: [0, -15, 2160],
        opacity: [1, 1, 0],
        scale: [1, 1, 1]
      },
      { duration: 1.2, ease: "easeOut", times: [0, 0.1, 1] }
    ).then(() => {
      giftEl.style.display = "none"; // optional, hide once gone
    });

    // gift: fade in while spinning fast -> slow to stop
    const giftAnim = animate(
      bananaEl,
      {
        rotate: [0, 2170, 2160, 2160, 2160],
        opacity: [0, 1, 1, 1, 1],
        scale: [0.1, 1, 1, 2.5, 1]
      },
      { duration: 1.5, ease: "easeOut", delay: 0.2, times: [0, 0.7, 0.8, 0.9, 1] }
    );

    const fireworksTimeout = setTimeout(() => {
      shootFireworks(400);
    }, 1450);

    // cleanup function (stop if unmounted)
    return () => {
      (bananaAnim as unknown as AnimationPlaybackControls)?.stop?.();
      (giftAnim as unknown as AnimationPlaybackControls)?.stop?.();
      clearTimeout(fireworksTimeout);
    };
  }, [showWrapingAnimation])

  const auth = getAuth();
  const user = auth.currentUser;
  const isLoggedIn = !!user;

  useEffect(() => {
    const fetchBanana = async () => {
      if (isFetching.current) {
        return;
      }

      isFetching.current = true;

      const redirectOnError = isLoggedIn ? "/your-bananas" : "/banana-stand";

      try {
        let response: {
          exists: boolean;
          error: string | null;
          banana: any;
          bananaOperations: any[]
          isWrapped: boolean;
          role: UserRole;
          canBananaBack: boolean;
        } = {
          exists: false,
          error: null,
          banana: null,
          bananaOperations: [],
          isWrapped: false,
          role: UserRole.Guest,
          canBananaBack: false
        }

        try {
          if (publicId) {
            response = await BananaApi.getBananaByPublicId(publicId, password);
          } else if (privateId) {
            response = await BananaApi.getBananaByPrivateId(privateId, password);
          }
        } catch (error: any) {
          console.error("Error fetching banana:", error);
          toast.error("Couldn't fetch banana");
          navigate(redirectOnError);
          return;
        }

        if (response.error) {
          toast.error(response.error);
          navigate(redirectOnError);
          return;
        }

        if (!response.exists) {
          toast.error("No such banana in the Bananaverse");
          navigate(redirectOnError);
          return;
        }

        setBanana(response.banana);
        setBananaOperations(response.bananaOperations);
        setIsWrapped(response.isWrapped);
        setRole(response.role);
        setCanBananaBack(response.canBananaBack);
        setIsLoading(false);
        logRelayEvent("banana_received_view", {
          role: response.role,
          wrapped: response.isWrapped,
          banana_type: response.banana?.type,
        });
        BananaApi.getRelayPassport(privateId || "", publicId || "", password || "")
          .then(setPassport)
          .catch(() => null);

      } catch (error) {
        console.error("Failed to fetch banana:", error);
      }
    };

    fetchBanana();
  }, [publicId, privateId, password, isLoggedIn]);

  const [lastBananaTransaction, setLastBananaTransaction] = useState<any>(null);

  useEffect(() => {
    let transaction = bananaOperations?.length > 0 ? bananaOperations[0] : null;
    if (transaction?.type === "open") {
      // Get actual send event
      transaction = bananaOperations?.length > 1 ? bananaOperations[1] : null;
    }
    setLastBananaTransaction(transaction);
    if (transaction?.message) setPublicMessage(transaction.message);
  }, [bananaOperations]);

  const bananType: BananaTypeProps | undefined = BananaType.getBananaTypeByKey(banana?.type);

  const handleBananaForward = () => {
    logRelayEvent("relay_started", { intent: relayIntent });
    setAction(Action.Forward);
  };
  const handleBananaBack = () => setAction(Action.SendBack);

  const chooseRelayIntent = (intent: RelayIntent) => {
    setRelayIntent(intent);
    setMessage(getDefaultMessage(intent));
    logRelayEvent("relay_intent_selected", { intent });
    setAction(Action.Forward);
  };

  const sendReaction = async (reaction: "heart" | "smile" | "banana") => {
    if (isSendingReaction || reactionSent === reaction) return;
    setIsSendingReaction(true);
    try {
      const result = await BananaApi.reactToBanana(privateId || "", publicId || "", password || "", reaction);
      setReactionSent(result.reaction);
      if (!result.alreadyReacted) {
        setPassport((current) => current ? { ...current, reactionCount: current.reactionCount + 1 } : current);
        logRelayEvent("banana_reaction_sent", { reaction });
      }
    } catch (error: any) {
      toast.error(error.message || "Couldn't send your reaction.");
    } finally {
      setIsSendingReaction(false);
    }
  };

  const isSender = role === UserRole.Sender;
  const isRecipient = role === UserRole.Recipient;
  const isGuest = role === UserRole.Guest;

  useEffect(() => {
    if (!isSender || !banana?.id) return;
    BananaApi.getMyJoyPost(banana.id).then((post) => {
      setIsJoyPublished(post.published);
      if (post.message) setPublicMessage(post.message);
    }).catch(() => null);
  }, [isSender, banana?.id]);

  const baseUrl = process.env.REACT_APP_PUBLIC_URL || window.location.origin
  const shareUrl = banana ? baseUrl + Links.getLinkToViewBanana(banana, lastBananaTransaction?.password) : "#";

  const shareText = `You've received a Tuttobanana digital gift! Click to open!`;

  const [copied, setCopied] = useState(false);

  const copyToClipboard = async () => {
    logRelayEvent("relay_share_opened", { channel: "copy" });
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      // no clipboard? fallback
      const ta = document.createElement("textarea");
      ta.value = shareUrl; document.body.appendChild(ta);
      ta.select(); document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true); setTimeout(() => setCopied(false), 1600);
    }
  };

  const smsHref = `sms:?&body=${encodeURIComponent(`${shareText} ${shareUrl}`)}`;
  const emailHref = `mailto:?subject=${encodeURIComponent("A Tuttobanana for you 🍌")}&body=${encodeURIComponent(`${shareText}\n\n${shareUrl}`)}`;
  const facebookHref = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`;

  const copyForStory = async (platform: string) => {
    await copyToClipboard();
    toast.success(`Link copied — paste it into your ${platform} Story.`);
  };

  const shareBtn = "w-full inline-flex items-center justify-center gap-2 rounded-full px-4 py-2 text-sm sm:text-base font-semibold bg-white text-gray-900 border-2 border-yellow-400 hover:bg-yellow-400 hover:border-transparent transition shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 cursor-pointer active:bg-yellow-300"

  const publishJoy = async () => {
    if (!banana?.id || !publicMessage.trim()) return;
    setIsPublishingJoy(true);
    try {
      await BananaApi.publishJoyPost(banana.id, publicMessage.trim());
      setIsJoyPublished(true);
      toast.success("Shared anonymously with the Bananaverse.");
    } catch (error: any) {
      toast.error(error.message || "Couldn't publish this message.");
    } finally {
      setIsPublishingJoy(false);
    }
  };

  const unpublishJoy = async () => {
    if (!banana?.id) return;
    setIsPublishingJoy(true);
    try {
      await BananaApi.unpublishJoyPost(banana.id);
      setIsJoyPublished(false);
      toast.success("Removed from the public Bananaverse.");
    } catch (error: any) {
      toast.error(error.message || "Couldn't remove this message.");
    } finally {
      setIsPublishingJoy(false);
    }
  };

  let senderNameText = User.getSenderName(lastBananaTransaction)

  return (
    <>
      <title>You've received a Tuttobanana! (Click to open)</title>
      <meta property="og:title" content="You've received a Tuttobanana digital gift!" />
      <meta property="og:description" content="Click to open your Tuttobanana 🍌" />
      <meta property="og:image" content={`${process.env.PUBLIC_URL}/logos/logo512.png`} />
      <meta property="og:url" content={window.location.origin + window.location.pathname} />
      <meta name="description" content="View your Tuttobanana - a tiny digital gift with a big feeling. Unwrap your banana to read the message, then banana forward or banana back to keep the bananergy going." />
      {isLoading && <div className="min-h-screen bg-gray-100 text-gray-900 flex justify-center py-10">
        <LoadingSpinner label="Loading banana..." size="md" />
      </div>}
      {!isLoading && <div className="min-h-screen bg-gray-100 text-gray-900 px-6 py-20 sm:px-12 overflow-x-hidden">
        {action === Action.View && <div className="max-w-5xl mx-auto space-y-16">
          <div className="max-w-2xl mx-auto bg-white rounded-xl shadow p-6 space-y-6">
            {isWrapped && <div className="relative">
              {/* Public badge (top-right) */}
              {banana.isPublic && (
                <span className="absolute top-0 right-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800 border border-yellow-300">
                  Public
                </span>
              )}

              {/* Title */}
              <h2 className="text-2xl font-bold text-gray-900 text-center mb-6">
                {isRecipient && <>You've Got a Banana Present! 🎁🍌</>}
                {isSender && <>Your Present Is Ready to Be Shared!🎁🍌</>}
                {isGuest && <>This Present Is Waiting to Be Opened! 🎁🍌</>}
                {(isRecipient || isGuest) && <p className="mt-2 text-lg">
                  It's a gift from {senderNameText} in {lastBananaTransaction?.senderLocation || "Bananaland"}
                </p>}
                {isSender && lastBananaTransaction?.recipientName && <p className="text-center text-lg">
                  To {lastBananaTransaction?.recipientName}
                </p>}
              </h2>

              {/* Present image centered */}
              <div className="flex justify-center">
                <div className="relative w-48 h-48 overflow-visible">
                  <img
                    src={bananType?.img}
                    alt={bananType?.title}
                    ref={bananaImgRef}
                    style={{ opacity: isWrapped ? 0 : 1 }}
                    className="absolute inset-0 m-auto w-48 h-48 object-contain rounded-md"
                  />
                  <img
                    src="/logos/bananas/present.png"
                    alt="Banana Present"
                    ref={bananaPresentImgRef}
                    style={{ opacity: isWrapped ? 1 : 0 }}
                    onClick={() => isRecipient && !isLoadingAction ? unwrapBanana() : null}
                    className={`w-48 h-48 object-contain rounded-md anticipation-shake ${isRecipient && !isLoadingAction ? "cursor-pointer" : ""}`}
                  />
                  <ConfettiCanvas top={-300} left={-200} />
                </div>
              </div>
            </div>}
            {!isWrapped && <div className="relative flex flex-col sm:flex-row items-top gap-6">
              <img
                src={bananType?.img}
                alt={bananType?.title}
                className="w-48 h-48 object-contain rounded-md"
              />
              <div className="text-center sm:text-left flex-1 w-full min-w-0">
                <div className="flex items-center justify-between w-full">
                  <h2 className="text-2xl font-bold text-gray-900 truncate">
                    {bananType?.title}
                  </h2>
                  {banana.isPublic && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800 border border-yellow-300 shrink-0">
                      Public
                    </span>
                  )}
                </div>
                <p className="text-gray-600 text-left mt-2">{bananType?.description}</p>

                {/* Sender */}
                <div className="mt-4 flex items-center gap-2">
                  <span className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1 shadow-sm">
                    {lastBananaTransaction?.senderName && <span className="text-sm font-semibold text-gray-900">
                      {lastBananaTransaction?.senderName}
                    </span>}
                    {lastBananaTransaction?.sender?.username && (
                      <span className="text-sm text-gray-900">
                        @{lastBananaTransaction?.sender?.username}
                      </span>
                    )}
                    {!lastBananaTransaction?.senderName && !lastBananaTransaction?.sender?.username && (
                      <span className="text-sm text-gray-900">
                        Bananonymous
                      </span>
                    )}
                  </span>
                  {lastBananaTransaction?.senderLocation && (
                    <span className="text-sm text-gray-500">
                      from {lastBananaTransaction?.senderLocation}
                    </span>
                  )}
                </div>

                {/* Message */}
                {lastBananaTransaction?.message && (
                  <div className="mt-4">
                    <div className="relative rounded-xl border border-yellow-200 bg-yellow-50 p-4 shadow-sm">
                      {/* speech-bubble tail */}
                      <div className="absolute -top-2 left-6 h-4 w-4 rotate-45 border-l border-t border-yellow-200 bg-yellow-50" />
                      <p className="text-gray-800 whitespace-pre-line text-left">
                        {lastBananaTransaction?.message}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>}

            {isRecipient && isWrapped && <p className="text-center text-gray-900 font-semibold text-lg mb-4">
              Click below to unwrap your Tuttobanana! 🍌
            </p>}
            {isGuest && isWrapped && <div className="rounded-xl bg-yellow-50 p-4 text-center text-sm text-gray-700 ring-1 ring-yellow-200">
              This link is missing the current banana code. Ask the sender to share the newest link with you.
            </div>}

            <div>
              <p className="text-sm sm:text-base text-gray-700 italic border-l-4 border-yellow-400 pl-4 sm:pl-6 mb-8">
                {MessageType.SEND_BANANA_FUN_MESSAGES[funMessageIndex.current]}
              </p>
            </div>

            {isWrapped && isSender && <>
              {/* Share section */}
              <div className="mt-8 space-y-3">
                <h3 className="text-lg font-semibold text-gray-900">Share this banana of joy!</h3>
                <div className="rounded-xl bg-yellow-50 p-4 text-center ring-1 ring-yellow-200">
                  <p className="font-semibold text-gray-900">Your banana is traveling.</p>
                  <p className="mt-1 text-sm text-gray-600">Come back to see where it lands and what grows.</p>
                  <Link to="/your-bananas" className="mt-3 inline-block text-sm font-bold text-yellow-700 underline">
                    Track this banana
                  </Link>
                </div>

                <section className="rounded-2xl border-2 border-yellow-400 bg-gray-900 p-5 text-white shadow-lg sm:p-6">
                  <div className="flex items-start gap-3">
                    <span className="text-4xl" aria-hidden="true">🌍</span>
                    <div>
                      <p className="text-xs font-black uppercase tracking-[0.18em] text-yellow-300">Optional · Inspire others</p>
                      <h3 className="mt-1 text-2xl font-black">Share the joy with the world</h3>
                    </div>
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-gray-200">Your banana and delivery link stay private. If you choose to publish, the Bananaverse receives only the anonymous message preview below—never your name, handle, recipient, or city.</p>
                  <div className="mt-4 rounded-xl bg-white p-4 text-gray-900">
                    <label className="block text-sm font-black" htmlFor="public-joy-message">This is exactly what the public will see:</label>
                    <textarea
                      id="public-joy-message"
                      rows={3}
                      maxLength={150}
                      value={publicMessage}
                      onChange={(event) => setPublicMessage(event.target.value)}
                      className="mt-2 w-full rounded-xl border border-yellow-300 bg-yellow-50 p-3 text-gray-900"
                    />
                    <p className="mt-2 text-xs text-gray-500">Anyone on the internet may see this anonymous copy.</p>
                  </div>
                  {!isJoyPublished ? <button
                    type="button"
                    onClick={publishJoy}
                    disabled={isPublishingJoy || !publicMessage.trim()}
                    className="mt-4 w-full rounded-full bg-yellow-400 px-5 py-3 text-lg font-black text-gray-900 shadow transition hover:bg-yellow-300 disabled:opacity-50"
                  >
                    {isPublishingJoy ? "Publishing…" : "🌍 Yes—share this anonymous message"}
                  </button> : <div className="mt-4 rounded-xl bg-white p-4 text-center text-gray-900 ring-2 ring-green-400">
                    <p className="font-black text-green-800">Shared with the Bananaverse ✓</p>
                    <button type="button" onClick={unpublishJoy} disabled={isPublishingJoy} className="mt-2 text-sm font-semibold underline">
                      Remove public copy
                    </button>
                  </div>}
                  {!isJoyPublished && <p className="mt-3 text-center text-sm text-gray-300">Prefer to keep it between you two? Simply skip this—nothing is published.</p>}
                </section>

                {/* Link + Copy */}
                <div className="flex items-stretch gap-2">
                  <input
                    readOnly
                    value={shareUrl}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 overflow-x-auto"
                  />
                  <button
                    onClick={copyToClipboard}
                    className="rounded-lg px-3 py-2 text-sm font-semibold bg-yellow-400 text-gray-900 hover:bg-yellow-300 transition shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 cursor-pointer"
                  >
                    {copied ? "Copied!" : "Copy"}
                  </button>
                </div>

                {/* Quick share */}
                <div className="flex flex-col gap-2 max-w-xs mx-auto">
                  <a href={smsHref} className={shareBtn}>
                    <FiMessageSquare className="text-base" />
                    Text
                  </a>

                  <a href={emailHref} className={shareBtn}>
                    <FiMail className="text-base" />
                    Email
                  </a>

                  <a href={whatsappHref} target="_blank" rel="noopener noreferrer" className={shareBtn}>
                    <FaWhatsapp className="text-base" />
                    WhatsApp
                  </a>

                  <button onClick={() => copyForStory("Instagram")} className={shareBtn}>
                    <FiInstagram className="text-base" />
                    Instagram
                  </button>

                  <button onClick={() => copyForStory("TikTok")} className={shareBtn}>
                    <FaTiktok className="text-base" />
                    TikTok
                  </button>

                  <a href={facebookHref} target="_blank" rel="noopener noreferrer" className={shareBtn}>
                    <FaFacebook className="text-base" />
                    Facebook
                  </a>

                  <button onClick={() => copyForStory("Snapchat")} className={shareBtn}>
                    <FaSnapchatGhost className="text-base" />
                    Snapchat
                  </button>
                </div>

              </div>
            </>}

            {isWrapped && isRecipient && <>
              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={unwrapBanana}
                  disabled={isLoadingAction}
                  className={`px-6 py-3 bg-yellow-400 text-gray-900 font-semibold rounded-md shadow transition 
                    ${isLoadingAction ? "opacity-60 cursor-not-allowed" : "btn-banana-pulse cursor-pointer"}`}
                >
                  {isLoadingAction ? (
                    <span className="flex items-center justify-center gap-2">
                      <FiLoader className="animate-spin" />
                      🎁 Unwrapping...
                    </span>
                  ) : (
                    "🎁 Open the Present!"
                  )}
                </button>
              </div>
            </>}

            {!isWrapped && isRecipient && <div className="space-y-6">
              <section className="border-t border-gray-100 pt-6 text-center">
                <p className="text-sm font-bold uppercase tracking-wider text-gray-500">
                  {reactionSent ? "They know it landed" : "Let them know it landed"}
                </p>
                <div className="mt-3 flex justify-center gap-3">
                  {[
                    ["heart", "❤️", "That meant a lot"],
                    ["smile", "😊", "Made me smile"],
                    ["banana", "🍌", "Perfect banana"],
                  ].map(([key, emoji, label]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => sendReaction(key as "heart" | "smile" | "banana")}
                      disabled={isSendingReaction || reactionSent === key}
                      aria-pressed={reactionSent === key}
                      className={`flex min-w-0 flex-1 flex-col items-center rounded-xl border px-2 py-3 text-xs font-semibold transition sm:text-sm ${reactionSent === key ? "border-yellow-400 bg-yellow-100" : "border-gray-200 bg-white hover:border-yellow-400 hover:bg-yellow-50"} ${reactionSent && reactionSent !== key ? "opacity-70" : ""}`}
                    >
                      <span className="mb-1 text-2xl">{emoji}</span>
                      <span>{label}</span>
                    </button>
                  ))}
                </div>
                {reactionSent && <p className="mt-3 text-sm font-semibold text-gray-600" role="status">
                  Your reaction joined the journey. Tap another to change it.
                </p>}
              </section>

              <BananaPassport passport={passport} showMilestone={isSender} />

              {isSender && auth.currentUser?.isAnonymous && <section className="rounded-2xl border border-gray-200 bg-gray-50 p-5 text-center">
                <h3 className="font-black text-gray-900">Want to keep this journey?</h3>
                <p className="mt-1 text-sm text-gray-600">Link an email or Google account to find it later in Your Bananas. Sending stays free.</p>
                <Link to="/log-in" className="mt-3 inline-flex rounded-full border-2 border-gray-900 bg-white px-5 py-2 text-sm font-bold text-gray-900">Save to Your Bananas</Link>
              </section>}

              {data.settings.relayEnabled !== false ? <section className="rounded-2xl bg-gray-900 p-5 text-white sm:p-6">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-yellow-300">Keep it moving</p>
                <h3 className="mt-2 text-2xl font-black">Who should get the next banana?</h3>
                <p className="mt-2 text-sm text-gray-300">Your message stays private. Only the journey continues.</p>
                <div className="mt-5 space-y-3">
                  {RELAY_INTENTS.map((intent) => (
                    <button
                      key={intent.key}
                      type="button"
                      onClick={() => chooseRelayIntent(intent.key)}
                      className="flex w-full items-center gap-3 rounded-xl bg-white px-4 py-3 text-left text-gray-900 shadow-sm transition hover:-translate-y-0.5 hover:bg-yellow-50"
                    >
                      <span className="text-2xl">{intent.emoji}</span>
                      <span className="flex-1">
                        <span className="block font-bold">{intent.title}</span>
                        <span className="block text-sm text-gray-500">{intent.subtitle}</span>
                      </span>
                      <span className="text-xl text-yellow-600">→</span>
                    </button>
                  ))}
                </div>
              </section> : <section className="rounded-2xl border border-amber-300 bg-amber-50 p-5 text-center text-amber-950">
                <p className="font-black">Banana Relay is temporarily paused.</p>
                <p className="mt-1 text-sm">You can still react or send a reply back.</p>
              </section>}

              <div className="flex flex-col items-center gap-3 border-t border-gray-100 pt-5 sm:flex-row sm:justify-center">
                <button
                  onClick={handleBananaForward}
                  disabled={data.settings.relayEnabled === false}
                  className="w-full rounded-full bg-yellow-400 px-6 py-3 text-center font-bold text-gray-900 shadow-sm transition hover:bg-yellow-300 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
                >
                  Keep This Banana Moving 🍌
                </button>
                {canBananaBack && <button
                  onClick={handleBananaBack}
                  className="w-full rounded-full border-2 border-yellow-400 bg-white px-5 py-2.5 text-center font-semibold text-gray-900 transition hover:bg-yellow-50 sm:w-auto"
                >
                  Banana Back — Send a reply
                </button>}
              </div>
            </div>}
          </div>
        </div>}
        {(action === Action.Forward || action === Action.SendBack) && <SendBananaMessageView 
          funMessageIndex={funMessageIndex.current}
          isLoading={isLoadingAction}
          isPublic={banana.isPublic}
          senderName={senderName}
          recipientName={recipientName}
          onCloseClick={() => isLoadingAction ? null : setAction(Action.View)}
          message={message}
          title={TITLES[action]}
          selectedBanana={bananType}
          setSenderName={setSenderName}
          setRecipientName={setRecipientName}
          setMessage={setMessage}
          sendBanana={sendBanana}
          hideCloseButton={isLoadingAction}
          showWrapingAnimation={showWrapingAnimation}
          suggestedMessages={action === Action.Forward ? getSuggestedMessages(relayIntent) : []}
        />}
        {bananaOperations.length > 1 && (
          <ViewBananaHistory bananaOperations={bananaOperations} />
        )}
      </div>}
      
    </>
  );
};

export default ViewBananaPage;
